/**
 * THE BOTTOM GROUP CHURN, TOLD IN ONE LINE.
 *
 * Howard, 5 Oct 2026: "I hate that moment and the repetitive rolls that no one
 * can tell what is happening upon their results." The Churn (EZRA-S2 §3.12,
 * `bottomGroupChurn.ts`) stays exactly as printed — its dice are still thrown
 * and recorded, and every result is unchanged. What changes is the telling: the
 * table no longer walks its six-odd throws one by one (`raceDice.ts` marks them
 * `churn`), and this module turns the result into one plain sentence for the
 * "Meanwhile" ticker, the dice table and the race radio.
 *
 * Reads the turn's recorded result only. Draws no dice.
 */

import type { DuelResult } from './duel.ts';
import type { TurnResult } from './turn.ts';

export interface ChurnMove {
  driverId: string;
  name: string;
  /** Where he was when the shuffle began, and where it left him (1-based). */
  from: number;
  to: number;
}

export interface ChurnStory {
  /** Men the shuffle moved UP, net of every swap, best finishing spot first. */
  moves: ChurnMove[];
  /** Men who won the Porch run at 19th and banked a bonus chip. */
  porch: { driverId: string; name: string; passedId: string; passedName: string }[];
  /** Laps-down men who got their lap back in the shuffle. */
  lapBack: { driverId: string; name: string; to: number }[];
  /** The Churn played as a duel (black 6). */
  duel: { instigatorId: string; instigatorName: string; targetId: string; targetName: string; won: boolean; declined: boolean } | null;
}

/** What the Churn did this turn, or null when no Churn happened. */
export function churnStory(turn: TurnResult | undefined): ChurnStory | null {
  const c = turn?.churn;
  if (!turn || !c || !c.occurred) return null;
  const names = new Map<string, string>();
  for (const car of [...turn.state.grid, ...(turn.state.retired ?? [])]) names.set(car.card.driverId, car.card.name);
  for (const ch of c.challenges) {
    names.set(ch.challengerId, ch.challengerName);
    if (ch.defenderId && ch.defenderName) names.set(ch.defenderId, ch.defenderName);
  }
  const nameOf = (id: string) => names.get(id) ?? 'a backmarker';

  const story: ChurnStory = { moves: [], porch: [], lapBack: [], duel: null };
  // Where each man was when the shuffle first reached him (the challenge
  // records it), and where the Churn's own authoritative order left him. A man
  // who passed two cars is one move ("from 22nd to 20th"), not two half-stories.
  const start = new Map<string, number>();
  const seen = (id: string, pos: number) => {
    if (!start.has(id)) start.set(id, pos);
  };
  for (const ch of c.challenges) {
    if (ch.ignored || ch.voided || !ch.defenderId || ch.defenderPosition === null) continue;
    if (ch.porch) {
      if (ch.success && ch.awardedBgChip) story.porch.push({ driverId: ch.challengerId, name: ch.challengerName, passedId: ch.defenderId, passedName: ch.defenderName ?? nameOf(ch.defenderId) });
      continue;
    }
    seen(ch.challengerId, ch.from);
    seen(ch.defenderId, ch.defenderPosition);
    if (ch.success && ch.ldsg?.escaped) story.lapBack.push({ driverId: ch.challengerId, name: ch.challengerName, to: c.finalOrder.indexOf(ch.challengerId) + 1 });
  }
  const back = new Set(story.lapBack.map((l) => l.driverId));
  for (const [id, from] of start) {
    const at = c.finalOrder.indexOf(id);
    if (at < 0 || back.has(id)) continue;
    if (at + 1 < from) story.moves.push({ driverId: id, name: nameOf(id), from, to: at + 1 });
  }
  story.moves.sort((a, b) => a.to - b.to);
  if (c.asDuel && c.duelInstigatorId && c.duelTargetId) {
    const duel = turn.events.find((e) => e.eventType === 'duel' && e.momentTag.startsWith('churn-duel.')) as DuelResult | undefined;
    story.duel = {
      instigatorId: c.duelInstigatorId,
      instigatorName: nameOf(c.duelInstigatorId),
      targetId: c.duelTargetId,
      targetName: nameOf(c.duelTargetId),
      won: !!duel?.won,
      declined: !!duel?.declined,
    };
  }
  return story;
}

const ordinal = (n: number) => {
  const s = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${s}`;
};

const joinAnd = (parts: string[]) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`);

/** How many climbers the one line names before it says "and N others". */
export const CHURN_LINE_MOVES = 3;

/**
 * One plain sentence for what the shuffle at the back did, or null when no
 * Churn happened. `short` turns a full name into the name the screen uses.
 */
export function churnLine(turn: TurnResult | undefined, short: (id: string, name: string) => string = (_id, name) => name): string | null {
  const s = churnStory(turn);
  if (!s) return null;
  if (s.duel) {
    const I = short(s.duel.instigatorId, s.duel.instigatorName);
    const T = short(s.duel.targetId, s.duel.targetName);
    if (s.duel.declined) return `At the back, ${I} looks at a duel with ${T}, then thinks better of it.`;
    return s.duel.won
      ? `At the back, ${I} dives under ${T} in a duel, and ${T} is in trouble.`
      : `At the back, ${I} tries a duel on ${T} and gets it wrong. ${I} is the one in trouble.`;
  }
  const named = s.moves.slice(0, CHURN_LINE_MOVES).map((m, i) => `${short(m.driverId, m.name)}${i === 0 ? ' climbs' : ''} from ${ordinal(m.from)} to ${ordinal(m.to)}`);
  const others = s.moves.length - CHURN_LINE_MOVES;
  if (others > 0) named.push(`${others} ${others === 1 ? 'other moves' : 'others move'} up`);
  const extra: string[] = [];
  for (const p of s.porch) extra.push(`${short(p.driverId, p.name)} beats ${short(p.passedId, p.passedName)} in a run at 19th and banks a bonus chip.`);
  for (const l of s.lapBack) extra.push(`${short(l.driverId, l.name)} gets his lap back and runs ${ordinal(l.to)}.`);
  if (!named.length && !extra.length) return 'At the back, the shuffle changes nothing.';
  return [...(named.length ? [`At the back, ${joinAnd(named)}.`] : []), ...extra].join(' ');
}
